const AWS_STORAGE_GUARD_FLAG = '__tppAwsStorageGuard__';
const AWS_KEY_PATTERN = /(^aws|^cognito|amazonaws|cognito|aws\.)/i;

const createMemoryStorage = () => {
  const store = new Map();

  return {
    getItem(key) {
      return store.has(key) ? store.get(key) : null;
    },
    setItem(key, value) {
      store.set(key, String(value));
    },
    removeItem(key) {
      store.delete(key);
    },
    key(index) {
      return Array.from(store.keys())[index] ?? null;
    },
    clear() {
      store.clear();
    },
    get length() {
      return store.size;
    }
  };
};

const resolveStorage = (accessor) => {
  try {
    return accessor();
  } catch (error) {
    return null;
  }
};

export const initializeAwsStorageGuard = (options = {}) => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (window[AWS_STORAGE_GUARD_FLAG]) {
    return true;
  }

  const localStorageRef = resolveStorage(() => window.localStorage);
  if (!localStorageRef) {
    return false;
  }

  const sessionStorageRef =
    resolveStorage(() => window.sessionStorage) ?? createMemoryStorage();

  const shouldIsolate =
    options.shouldIsolate ??
    ((key) => Boolean(key && AWS_KEY_PATTERN.test(String(key))));

  const storageProto = Object.getPrototypeOf(localStorageRef);
  if (!storageProto) {
    return false;
  }

  if (storageProto[AWS_STORAGE_GUARD_FLAG]) {
    window[AWS_STORAGE_GUARD_FLAG] = true;
    return true;
  }

  const originalSetItem = storageProto.setItem;
  const originalGetItem = storageProto.getItem;
  const originalRemoveItem = storageProto.removeItem;
  const originalKey = storageProto.key;
  const originalClear = storageProto.clear;

  const removeAwsFromSession = () => {
    const keys = [];
    for (let index = 0; index < sessionStorageRef.length; index += 1) {
      const key = sessionStorageRef.key(index);
      if (key) {
        keys.push(key);
      }
    }
    keys.forEach((key) => {
      if (shouldIsolate(key)) {
        sessionStorageRef.removeItem(key);
      }
    });
  };

  const migrateKeys = () => {
    const keys = [];
    for (let index = 0; index < localStorageRef.length; index += 1) {
      const key = originalKey.call(localStorageRef, index);
      if (key) {
        keys.push(key);
      }
    }
    keys.forEach((key) => {
      if (!shouldIsolate(key)) {
        return;
      }
      const value = originalGetItem.call(localStorageRef, key);
      if (value !== null && value !== undefined) {
        sessionStorageRef.setItem(key, value);
      }
      originalRemoveItem.call(localStorageRef, key);
    });
  };

  storageProto.setItem = function setItem(key, value) {
    if (this === localStorageRef && shouldIsolate(key)) {
      sessionStorageRef.setItem(key, value);
      originalRemoveItem.call(this, key);
      return;
    }
    originalSetItem.call(this, key, value);
  };

  storageProto.getItem = function getItem(key) {
    if (this === localStorageRef && shouldIsolate(key)) {
      const sessionValue = sessionStorageRef.getItem(key);
      if (sessionValue !== null && sessionValue !== undefined) {
        return sessionValue;
      }
    }
    return originalGetItem.call(this, key);
  };

  storageProto.removeItem = function removeItem(key) {
    if (this === localStorageRef && shouldIsolate(key)) {
      sessionStorageRef.removeItem(key);
    }
    originalRemoveItem.call(this, key);
  };

  storageProto.clear = function clear() {
    if (this === localStorageRef) {
      removeAwsFromSession();
    }
    originalClear.call(this);
  };

  migrateKeys();
  storageProto[AWS_STORAGE_GUARD_FLAG] = true;
  window[AWS_STORAGE_GUARD_FLAG] = true;
  return true;
};

export { AWS_STORAGE_GUARD_FLAG, AWS_KEY_PATTERN };
